<template>
    <div class="grafico-container grafico-destaque">
        <div class="grafico-header">
            <h3 class="grafico-titulo">Índice de Performance de Custo (IPC)</h3>
            <GraficoInfoIcon 
                titulo="Índice de Performance de Custo (IPC)"
                descricao="O IPC mede a eficiência dos projetos em relação ao custo. Calculado pela relação entre o custo planejado e o custo real. Valores próximos ou acima de 1 indicam boa performance de custo. Valores abaixo de 1 indicam que o projeto está acima do orçamento planejado."
                formula="IPC = Custo Planejado / Custo Real"
            />
        </div>
        <div v-if="!temDados" class="sem-dados">Nenhum dado disponível</div>
        <Bar v-else :data="chartData" :options="chartOptions" />
    </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import GraficoInfoIcon from './GraficoInfoIcon.vue';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default {
    name: 'GraficoIPC',
    components: { Bar, GraficoInfoIcon },
    props: {
        projetos: {
            type: Array,
            default: () => []
        }
    },
    emits: ['clicar-projeto'],
    computed: {
        projetosComIPC() {
            return this.projetos
                .map(p => ({
                    projeto: p,
                    ipc: this.calcularIPC(p)
                }))
                .filter(item => item.ipc !== null && item.ipc !== undefined)
                .sort((a, b) => b.ipc - a.ipc)
                .slice(0, 30);
        },
        temDados() {
            return this.projetosComIPC.length > 0;
        },
        chartData() {
            const projetos = this.projetosComIPC;
            
            return {
                labels: projetos.map(item => this.formatarNomeProjeto(item.projeto)),
                datasets: [{
                    label: 'IPC',
                    data: projetos.map(item => item.ipc),
                    backgroundColor: projetos.map(item => {
                        if (item.ipc >= 1.0) return 'rgba(46, 204, 113, 0.6)';
                        if (item.ipc >= 0.8) return 'rgba(52, 152, 219, 0.6)';
                        if (item.ipc >= 0.5) return 'rgba(241, 196, 15, 0.6)';
                        return 'rgba(231, 76, 60, 0.6)';
                    }),
                    borderColor: projetos.map(item => {
                        if (item.ipc >= 1.0) return 'rgba(46, 204, 113, 1)';
                        if (item.ipc >= 0.8) return 'rgba(52, 152, 219, 1)';
                        if (item.ipc >= 0.5) return 'rgba(241, 196, 15, 1)';
                        return 'rgba(231, 76, 60, 1)';
                    }),
                    borderWidth: 2
                }]
            };
        },
        chartOptions() {
            const self = this;
            const projetosComIPC = this.projetosComIPC;
            
            return {
                responsive: true,
                maintainAspectRatio: false,
                onClick: (event, elements) => {
                    if (elements.length > 0) {
                        const index = elements[0].index;
                        const item = projetosComIPC[index];
                        if (item) {
                            self.$emit('clicar-projeto', item.projeto.id);
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'IPC'
                        },
                        ticks: {
                            callback: (value) => value.toFixed(2)
                        }
                    },
                    x: {
                        title: {
                            display: true,
                            text: 'Projetos'
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const item = projetosComIPC[context.dataIndex];
                                const custoReal = parseFloat(item.projeto.custo_total || 0);
                                const custoPlanejado = parseFloat(item.projeto.pcm?.estimativa_custo || 0);
                                
                                return [
                                    `IPC: ${context.parsed.y.toFixed(2)}`,
                                    `Custo Real: R$ ${custoReal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                                    `Custo Planejado: R$ ${custoPlanejado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                ];
                            },
                            footer: () => 'Clique para ver detalhes'
                        }
                    }
                }
            };
        }
    },
    methods: {
        formatarNomeProjeto(projeto, maxLength = 25) {
            const nome = projeto.nome || 'Sem nome';
            const codigoPCM = projeto.pcm?.codigo || '';
            const nomeCompleto = codigoPCM ? `${codigoPCM} - ${nome}` : nome;
            return nomeCompleto.length > maxLength ? nomeCompleto.substring(0, maxLength) + '...' : nomeCompleto;
        },
        calcularIPC(projeto) {
            const custoReal = parseFloat(projeto.custo_total || 0);
            const custoPlanejado = parseFloat(projeto.pcm?.estimativa_custo || 0);
            
            if (custoReal === 0) return null;
            if (custoPlanejado === 0) {
                // Se não há custo planejado do PCM, retornar null
                return null;
            }
            
            return Math.round((custoPlanejado / custoReal) * 100) / 100;
        }
    }
};
</script>

<style scoped>
.grafico-container {
    background: var(--cor-bg, white);
    border: 1px solid var(--cor-separador, #ddd);
    border-radius: 8px;
    padding: 1.5rem;
    height: 600px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
}

.grafico-destaque {
    border: 2px solid var(--cor-primaria, #3697d3);
    box-shadow: 0 4px 12px rgba(54, 151, 211, 0.2);
}

.grafico-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.grafico-titulo {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--cor-fonte, #333);
}

.sem-dados {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
}
</style>

